import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Printer } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ZplPrinterListStatus } from "@/components/ZplPrinterSelect";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { formatZplPrinterOptionLabel, ZPL_PRINTERS_QUERY_KEY } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { cn, HORIZONTAL_ELLIPSIS } from "@/lib/utils";

export interface AdminZplPrinterRow {
  id: string;
  name: string;
  relay_id: string | null;
  description: string | null;
  location: string | null;
  station: string | null;
  station_id: number | null;
  station_site: string | null;
  model: string | null;
  identifier: string | null;
  dpi: number | null;
  label_size: string | null;
  aliases: string[];
  sort_order: number;
  is_active: boolean;
  last_synced_at: string | null;
  display_name: string;
  online?: boolean | null;
  status_error?: string | null;
}

type RegistryResponse = {
  printers: AdminZplPrinterRow[];
  lastSyncedAt: string | null;
};

type PrintServerStockCheckRow = {
  name: string;
  hubLabelSize: string | null;
  relayLabelSize: string | null;
  status: "match" | "differs" | "hub_only" | "relay_only";
};

type PrintServerStockCheck = {
  ok?: boolean;
  applied: number;
  error?: string;
  matched?: number;
  rows?: PrintServerStockCheckRow[];
};

type DraftRow = {
  stationId: string;
  model: string;
  stockWidth: string;
  stockHeight: string;
  dpi: string;
};

const DPI_OPTIONS = [
  { value: "203", label: "203" },
  { value: "300", label: "300" },
  { value: "600", label: "600" },
] as const;

const DENSE_CONTROL = "h-8 text-xs";

function stockLabel(value: string | null): string {
  return value == null || value === "" ? "unset" : value;
}

function describePrintServerCheck(result: PrintServerStockCheck): string {
  if (result.ok === false) {
    return result.error ?? "Could not update the print server.";
  }
  const problems = (result.rows ?? []).filter((row) => row.status !== "match");
  if (problems.length === 0) {
    const count = result.matched ?? 0;
    return count === 1
      ? "Print server matches this page (1 queue)."
      : `Print server matches this page (${count} queues).`;
  }
  const lines = problems.map((row) => {
    if (row.status === "differs") {
      return `${row.name} is ${stockLabel(row.relayLabelSize)} on the print server and ${stockLabel(row.hubLabelSize)} on this page.`;
    }
    if (row.status === "relay_only") {
      return `${row.name} is on the print server and not on this page yet.`;
    }
    return `${row.name} is on this page and not on the print server.`;
  });
  return lines.join(" ");
}

function parseStockParts(labelSize: string | null): { width: string; height: string } {
  if (labelSize == null || labelSize.trim() === "") return { width: "", height: "" };
  const match = /^(\d+(?:\.\d+)?)\s*[x\u00D7]\s*(\d+(?:\.\d+)?)$/i.exec(labelSize.trim());
  if (match == null) return { width: "", height: "" };
  return { width: match[1]!, height: match[2]! };
}

function draftsMatch(left: DraftRow, right: DraftRow): boolean {
  return (
    left.stationId === right.stationId &&
    left.model === right.model &&
    left.stockWidth === right.stockWidth &&
    left.stockHeight === right.stockHeight &&
    left.dpi === right.dpi
  );
}

function toDraft(row: AdminZplPrinterRow): DraftRow {
  const stock = parseStockParts(row.label_size);
  return {
    stationId: row.station_id != null ? String(row.station_id) : "",
    model: row.model ?? "",
    stockWidth: stock.width,
    stockHeight: stock.height,
    dpi: row.dpi != null ? String(row.dpi) : "",
  };
}

function formatSyncedAt(value: string | null): string {
  if (value == null || value.trim() === "") return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function statusLabel(row: AdminZplPrinterRow): string {
  if (row.online === true) return "Online";
  if (row.online === false) return row.status_error === "disabled" ? "Disabled" : "Offline";
  return "Unknown";
}

function draftStockLabel(draft: DraftRow): string | undefined {
  const width = draft.stockWidth.trim();
  const height = draft.stockHeight.trim();
  if (width === "" || height === "") return undefined;
  return `${width}x${height}`;
}

function draftDisplayName(row: AdminZplPrinterRow, draft: DraftRow, stationName: string | undefined): string {
  return formatZplPrinterOptionLabel({
    name: row.name,
    station: stationName,
    model: draft.model.trim() === "" ? undefined : draft.model.trim(),
    identifier: row.identifier?.trim() ? row.identifier.trim() : undefined,
    labelSize: draftStockLabel(draft),
    displayName: undefined,
  });
}

function buildSaveBody(draft: DraftRow): {
  station_id: number | null;
  model: string | null;
  dpi: number | null;
  stock_width_inches: number | null;
  stock_height_inches: number | null;
} {
  const width = draft.stockWidth.trim() === "" ? null : Number(draft.stockWidth);
  const height = draft.stockHeight.trim() === "" ? null : Number(draft.stockHeight);
  return {
    station_id: draft.stationId === "" ? null : Number(draft.stationId),
    model: draft.model.trim() === "" ? null : draft.model.trim(),
    dpi: draft.dpi === "" ? null : Number(draft.dpi),
    stock_width_inches: width,
    stock_height_inches: height,
  };
}

export function ZplPrintersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";

  const registryQuery = useQuery<RegistryResponse, ApiError>({
    queryKey: ["admin", "zpl-printers"],
    queryFn: () => apiFetch<RegistryResponse>("/api/admin/zpl-printers"),
  });

  const {
    printersQuery,
    stations,
    station: labelStationId,
    setStation: setLabelStationId,
    unavailableMessage,
    documents,
  } = useZplPrintLocation({
    storageKey: "admin-zpl-printers",
    documents: [{ id: "test", label: "Test", labelSize: "4x2", dpi: 0 }],
  });

  const [draftById, setDraftById] = useState<Record<string, DraftRow>>({});
  const serverDraftRef = useRef<Record<string, DraftRow>>({});

  useEffect(() => {
    const list = registryQuery.data?.printers;
    if (!list) return;
    const previousServer = serverDraftRef.current;
    setDraftById((current) => {
      const next: Record<string, DraftRow> = {};
      for (const row of list) {
        const server = toDraft(row);
        const existing = current[row.id];
        const saved = previousServer[row.id];
        const unsaved = existing != null && saved != null && !draftsMatch(existing, saved);
        next[row.id] = unsaved ? existing : server;
      }
      return next;
    });
    const snapshot: Record<string, DraftRow> = {};
    for (const row of list) snapshot[row.id] = toDraft(row);
    serverDraftRef.current = snapshot;
  }, [registryQuery.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "zpl-printers"] });
    void queryClient.invalidateQueries({ queryKey: ZPL_PRINTERS_QUERY_KEY });
  };

  const [printServerNote, setPrintServerNote] = useState<{ text: string; warn: boolean } | null>(null);

  const checkMutation = useMutation({
    mutationFn: () =>
      apiFetch<PrintServerStockCheck>("/api/admin/zpl-printers/print-server-check", { method: "POST" }),
    onSuccess: (result) => {
      const text = describePrintServerCheck(result);
      const warn = result.ok === false || (result.rows ?? []).some((row) => row.status !== "match");
      setPrintServerNote({ text, warn });
      invalidate();
    },
    onError: (error: unknown) => {
      setPrintServerNote({
        text: error instanceof ApiError ? error.message : "Could not update the print server.",
        warn: true,
      });
    },
  });

  useEffect(() => {
    checkMutation.mutate();
    // Copy hub stock onto the print server when the page opens. Save repeats that copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMutation = useMutation({
    mutationFn: (input: {
      id: string;
      body: ReturnType<typeof buildSaveBody> | { is_active: boolean };
    }) =>
      apiFetch<{ printer: AdminZplPrinterRow; printServer?: PrintServerStockCheck }>(
        `/api/admin/zpl-printers/${input.id}`,
        {
          method: "PATCH",
          body: input.body,
        },
      ),
    onSuccess: (result) => {
      invalidate();
      if (result.printServer != null) {
        const text = describePrintServerCheck(result.printServer);
        const warn =
          result.printServer.ok === false
          || (result.printServer.rows ?? []).some((row) => row.status !== "match");
        setPrintServerNote({ text, warn });
      }
    },
  });

  const reprintMutation = useMutation({
    mutationFn: async (input: {
      id: string;
      draft: DraftRow;
      dirty: boolean;
      stationId: string;
    }) => {
      if (input.dirty) {
        await apiFetch<{ printer: AdminZplPrinterRow }>(`/api/admin/zpl-printers/${input.id}`, {
          method: "PATCH",
          body: buildSaveBody(input.draft),
        });
      }
      return apiFetch<{ ok: boolean }>(`/api/admin/zpl-printers/${input.id}/reprint-label`, {
        method: "POST",
        body: { station_id: Number(input.stationId) },
      });
    },
    onSuccess: () => invalidate(),
  });

  const serverRows = registryQuery.data?.printers ?? [];

  function updateDraft(id: string, patch: Partial<DraftRow>): void {
    setDraftById((current) => {
      const base = current[id];
      if (!base) return current;
      return { ...current, [id]: { ...base, ...patch } };
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHintContent={
            <>
              <p>
                Floor pages read this list. Set station, stock, model, and dpi here. Opening this page
                and saving a row copy those fields to the print server. Display name is Station Stock
                (Model Identifier). The identifier is assigned automatically from the print-server hardware
                id and stays on that printer when its IP changes. Physical printer labels print the identifier.
              </p>
              <p>
                Print server last copied its registry:{" "}
                <span className="font-medium text-slate-800">
                  {formatSyncedAt(registryQuery.data?.lastSyncedAt ?? null)}
                </span>
              </p>
            </>
          }
          hoverHintAriaLabel="ZPL printers registry help"
        >
          ZPL printers
        </PageTitleWithHelp>
        <InboundZplPrinterHeaderButton
          className="ml-auto"
          station={labelStationId}
          onStationChange={setLabelStationId}
          unavailableMessage={unavailableMessage}
          documents={documents}
        />
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Published printers</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <ZplPrinterListStatus printersQuery={printersQuery} />
          {checkMutation.isPending && printServerNote == null ? (
            <p className="text-sm text-slate-600">Copying printer config to the print server.</p>
          ) : null}
          {printServerNote != null ? (
            <p className={printServerNote.warn ? "text-sm text-amber-800" : "text-sm text-slate-700"}>
              {printServerNote.text}
            </p>
          ) : null}
          {reprintMutation.isError ? (
            <p className="text-sm text-amber-800">
              {reprintMutation.error instanceof ApiError
                ? reprintMutation.error.message
                : "Could not reprint the printer label."}
            </p>
          ) : null}
          {reprintMutation.isSuccess ? (
            <p className="text-sm text-slate-700">Printer label sent.</p>
          ) : null}
          {registryQuery.isError ? (
            <p className="text-sm text-amber-800">
              {registryQuery.error instanceof ApiError
                ? registryQuery.error.message
                : "Could not load printer registry."}
            </p>
          ) : null}
          {registryQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading{HORIZONTAL_ELLIPSIS}</p>
          ) : serverRows.length === 0 ? (
            <p className="text-sm text-slate-600">
              No printers published yet. The print server adds its queues to this list about once an hour.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-2 font-medium">Status</th>
                    <th className="px-2 py-2 font-medium">Display name</th>
                    <th className="px-2 py-2 font-medium">Station</th>
                    <th className="px-2 py-2 font-medium">Stock (in)</th>
                    <th className="px-2 py-2 font-medium">Model</th>
                    <th className="px-2 py-2 font-medium">Identifier</th>
                    <th className="px-2 py-2 font-medium">Dpi</th>
                    <th className="px-2 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {serverRows.map((row) => {
                    const draft = draftById[row.id] ?? toDraft(row);
                    const serverDraft = toDraft(row);
                    const dirty =
                      draft.stationId !== serverDraft.stationId ||
                      draft.model !== serverDraft.model ||
                      draft.stockWidth !== serverDraft.stockWidth ||
                      draft.stockHeight !== serverDraft.stockHeight ||
                      draft.dpi !== serverDraft.dpi;
                    const previewName = draftDisplayName(
                      row,
                      draft,
                      stations.find((station) => String(station.id) === draft.stationId)?.name,
                    );
                    const dpiOptions = [
                      ...(draft.dpi === "" ? [{ value: "", label: "Not set" }] : []),
                      ...DPI_OPTIONS.map((option) => ({
                        value: option.value,
                        label: option.label,
                      })),
                    ];
                    const lockedIdentifier = row.identifier?.trim() ?? "";
                    const canReprint = lockedIdentifier !== "" && labelStationId.trim() !== "";
                    const actionsBusy = saveMutation.isPending || reprintMutation.isPending;
                    return (
                      <tr key={row.id} className="border-b border-slate-100 align-middle">
                        <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{statusLabel(row)}</td>
                        <td className="px-2 py-2 font-medium text-slate-900 whitespace-nowrap">
                          {previewName}
                        </td>
                        <td className="px-2 py-2">
                          <ZplLocationSelect
                            id={`zpl-station-${row.id}`}
                            label="Station"
                            showLabel={false}
                            size="compact"
                            className="min-w-[10rem]"
                            value={draft.stationId}
                            siteCode="sgs"
                            disabled={!hubWritesAllowed}
                            onChange={(value) => updateDraft(row.id, { stationId: value })}
                            documents={documents}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              step="0.1"
                              min="0.1"
                              className={cn(DENSE_CONTROL, "w-14")}
                              aria-label={`Stock width inches for ${previewName}`}
                              value={draft.stockWidth}
                              disabled={!hubWritesAllowed}
                              onChange={(event) =>
                                updateDraft(row.id, { stockWidth: event.target.value })
                              }
                            />
                            <span className="text-slate-400">x</span>
                            <Input
                              type="number"
                              step="0.1"
                              min="0.1"
                              className={cn(DENSE_CONTROL, "w-14")}
                              aria-label={`Stock height inches for ${previewName}`}
                              value={draft.stockHeight}
                              disabled={!hubWritesAllowed}
                              onChange={(event) =>
                                updateDraft(row.id, { stockHeight: event.target.value })
                              }
                            />
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            className={cn(DENSE_CONTROL, "min-w-[5rem] w-24")}
                            value={draft.model}
                            disabled={!hubWritesAllowed}
                            aria-label={`Model for ${previewName}`}
                            onChange={(event) => updateDraft(row.id, { model: event.target.value })}
                          />
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span
                            className="inline-flex h-8 items-center text-sm font-medium text-slate-900"
                            title={
                              lockedIdentifier !== ""
                                ? "Locked to this print-server hardware id. Changing the printer IP does not reassign it."
                                : `Waiting for a print-server hardware id (prn_${HORIZONTAL_ELLIPSIS}). IP address is not used.`
                            }
                          >
                            {lockedIdentifier !== "" ? lockedIdentifier : "Pending hardware id"}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <HubTouchSelect
                            id={`zpl-dpi-${row.id}`}
                            aria-label={`Dpi for ${previewName}`}
                            size="compact"
                            className="min-w-[5rem] max-w-[6rem]"
                            value={draft.dpi}
                            disabled={!hubWritesAllowed}
                            options={dpiOptions}
                            onChange={(value) => updateDraft(row.id, { dpi: value })}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={!hubWritesAllowed || saveMutation.isPending}
                              onClick={() =>
                                saveMutation.mutate({
                                  id: row.id,
                                  body: { is_active: !row.is_active },
                                })
                              }
                            >
                              {row.is_active ? "Disable" : "Enable"}
                            </Button>
                            <HubSaveButton
                              type="button"
                              size="sm"
                              variant={dirty && hubWritesAllowed ? "primary" : "secondary"}
                              dirty={dirty}
                              disabled={!hubWritesAllowed || actionsBusy}
                              blockedHint={hubWritesAllowed ? null : adminReadOnlyTitle}
                              isLoading={saveMutation.isPending}
                              onClick={() => {
                                saveMutation.mutate({
                                  id: row.id,
                                  body: buildSaveBody(draft),
                                });
                              }}
                            >
                              Save
                            </HubSaveButton>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              title={
                                hubWritesAllowed
                                  ? canReprint
                                    ? dirty
                                      ? "Saves this row, then reprints the identifier label"
                                      : undefined
                                    : labelStationId.trim() === ""
                                      ? "Choose a location above"
                                      : "Waiting for a print-server hardware id"
                                  : adminReadOnlyTitle
                              }
                              disabled={!hubWritesAllowed || !canReprint || actionsBusy}
                              onClick={() =>
                                reprintMutation.mutate({
                                  id: row.id,
                                  draft,
                                  dirty,
                                  stationId: labelStationId,
                                })
                              }
                            >
                              {reprintMutation.isPending ? (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                              ) : (
                                <Printer className="mr-1 h-3 w-3" aria-hidden />
                              )}
                              Reprint
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={!hubWritesAllowed || actionsBusy}
                              onClick={() =>
                                apiFetch(`/api/admin/zpl-printers/${row.id}/find-me`, { method: "POST" }).catch(
                                  () => undefined,
                                )
                              }
                            >
                              Find me
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
